import React, { useEffect, useRef, useState } from "react";
import { useTranslate } from "@pankod/refine-core";
import { Form, Button, Modal, Select } from "@pankod/refine-antd";
import { ExclamationCircleOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import { ILanguage, LevelType } from "interfaces";
import { SortableLevelCard } from "./SortableLevelCard";
import { TriviaLevelConfig } from "./TriviaLevelConfig";
import { JigsortLevelConfig } from "./JigsortLevelConfig";
import { MinigameLevelConfig } from "./MinigameLevelConfig";

interface Props {
  languages: ILanguage[];
}

// Generate stable ids without depending on crypto.randomUUID (broader browser support).
let _seq = 0;
const newDndId = () =>
  `lvl-${Date.now().toString(36)}-${(_seq++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

/**
 * Per-level form contents (level_type Select + per-type config dispatch).
 * Hosted inside the editor's Modal — uses nested name paths so the Form.Items
 * register with the parent Form via React context (Modal renders via portal
 * but context flows through the React tree).
 */
const LevelFormFields: React.FC<{
  fieldName: number;
  languages: ILanguage[];
}> = ({ fieldName, languages }) => {
  const t = useTranslate();
  const levelType: LevelType | undefined = Form.useWatch(
    ["thematic", "levels", fieldName, "level_type"]
  );

  return (
    <>
      <Form.Item
        label={t("event.thematic.level_type")}
        name={[fieldName, "level_type"]}
        rules={[{ required: true }]}
        initialValue="trivia"
      >
        <Select
          options={[
            { value: "trivia", label: t("event.thematic.level_type_trivia") },
            { value: "jigsort", label: t("event.thematic.level_type_jigsort") },
            { value: "minigame", label: t("event.thematic.level_type_minigame") },
          ]}
        />
      </Form.Item>

      {levelType === "trivia" && <TriviaLevelConfig fieldName={fieldName} />}
      {levelType === "jigsort" && (
        <JigsortLevelConfig fieldName={fieldName} languages={languages} />
      )}
      {levelType === "minigame" && (
        <MinigameLevelConfig fieldName={fieldName} />
      )}
    </>
  );
};

/**
 * Reorderable grid of thematic event levels, backed by an antd Form.List under
 * the path ["thematic", "levels"].
 *
 * Layout: collapsed summary cards in a 4-column CSS Grid, drag-reorderable
 * across rows via @dnd-kit's rectSortingStrategy. Clicking Edit on any card
 * opens a single Modal hosting the per-level form. Save inside the modal
 * validates only that level (form.validateFields on the level's path); on
 * success, the modal closes. Delete inside the modal (or on the card) removes
 * the level. The modal cannot be closed any other way — no X, no Esc, no
 * backdrop click.
 *
 * Design notes:
 *  - DnD ids are NOT antd's `field.key` (which can flip during animations).
 *    We keep a parallel `ids: string[]` synced to the list length.
 *  - `level_number` is never a form field. The user only sees the displayed
 *    badge (idx + 1). The serializer (thematicTransform.ts) renumbers from
 *    array order on submit.
 *  - PointerSensor uses activationConstraint { distance: 5 } so click events
 *    on a card's Edit/Delete buttons aren't swallowed by a drag-start.
 */
export const LevelsEditor: React.FC<Props> = ({ languages }) => {
  const t = useTranslate();
  const form = Form.useFormInstance();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ids is in component state; we sync it to the form-list length whenever it
  // diverges (initial hydration, add, remove, reset on edit page).
  const [ids, setIds] = useState<string[]>([]);
  // Which card's modal is open. Tracked by DnD id (stable across reorders).
  const [editingId, setEditingId] = useState<string | null>(null);
  // DnD ids that were created via "Add level" and never confirmed via Save.
  // Cancel on a draft removes the level entirely; on an already-saved level
  // it restores the snapshot taken when the modal opened.
  const draftIdsRef = useRef<Set<string>>(new Set());
  // Snapshots of each level's form values at edit-open time, keyed by DnD id.
  // Used to revert when the user cancels.
  const snapshotsRef = useRef<Record<string, any>>({});

  // Hydrate ids when the levels form value first arrives (edit page).
  // Form.useWatch returns the current value reactively so this fires on resetFields.
  const levelsValue: any[] = Form.useWatch(["thematic", "levels"]) ?? [];
  useEffect(() => {
    setIds((prev) => {
      if (prev.length === levelsValue.length) return prev;
      const next = [...prev];
      while (next.length < levelsValue.length) next.push(newDndId());
      while (next.length > levelsValue.length) next.pop();
      return next;
    });
  }, [levelsValue.length]);

  return (
    <Form.List name={["thematic", "levels"]}>
      {(fields, { add, remove, move }) => {
        const handleDragEnd = (e: DragEndEvent) => {
          const { active, over } = e;
          if (!over || active.id === over.id) return;
          const from = ids.indexOf(String(active.id));
          const to = ids.indexOf(String(over.id));
          if (from < 0 || to < 0) return;
          move(from, to);
          setIds((prev) => arrayMove(prev, from, to));
        };

        const removeAt = (idx: number) => {
          const removedId = ids[idx];
          remove(fields[idx].name);
          setIds((prev) => prev.filter((_, i) => i !== idx));
          if (removedId) {
            draftIdsRef.current.delete(removedId);
            delete snapshotsRef.current[removedId];
          }
          if (editingId === removedId) setEditingId(null);
        };

        // Resolve the currently-editing level from editingId. Done inside the
        // render-prop so we have fresh `fields` after add/remove/reorder.
        const editingIdx = editingId ? ids.indexOf(editingId) : -1;
        const editingField = editingIdx >= 0 ? fields[editingIdx] : undefined;

        const openEdit = (id: string, idx: number) => {
          // Snapshot the level's current form values so Cancel can restore
          // them. Drafts (just-added levels) don't need a snapshot — Cancel
          // on a draft removes the level instead.
          if (!draftIdsRef.current.has(id)) {
            snapshotsRef.current[id] = form.getFieldValue([
              "thematic",
              "levels",
              idx,
            ]);
          }
          setEditingId(id);
        };

        const onSaveModal = async () => {
          if (!editingField || !editingId) return;
          try {
            await form.validateFields([
              ["thematic", "levels", editingField.name],
            ]);
            // Save committed: no longer a draft, no longer needs a snapshot.
            draftIdsRef.current.delete(editingId);
            delete snapshotsRef.current[editingId];
            setEditingId(null);
          } catch {
            // validation failed — errors render inside the modal, stay open
          }
        };

        const onCancelModal = () => {
          if (!editingId || !editingField || editingIdx < 0) return;
          if (draftIdsRef.current.has(editingId)) {
            // Brand-new level — discard it entirely.
            removeAt(editingIdx);
            return;
          }
          // Restore the snapshot taken at openEdit time.
          const snapshot = snapshotsRef.current[editingId];
          if (snapshot !== undefined) {
            form.setFields([
              {
                name: ["thematic", "levels", editingField.name],
                value: snapshot,
              },
            ]);
          }
          delete snapshotsRef.current[editingId];
          setEditingId(null);
        };

        const confirmDelete = (onConfirm: () => void) => {
          Modal.confirm({
            title: t("event.thematic.delete_level_confirm_title"),
            content: t("event.thematic.delete_level_confirm_content"),
            icon: <ExclamationCircleOutlined />,
            okText: t("event.thematic.delete_level"),
            okType: "danger",
            cancelText: t("event.thematic.cancel_level"),
            onOk: onConfirm,
          });
        };

        return (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={ids} strategy={rectSortingStrategy}>
                <div
                  style={{
                    display: "grid",
                    // minmax(0, 1fr) instead of 1fr so columns don't grow to
                    // fit min-content — otherwise a wide minigame JSON would
                    // stretch its column and defeat the cell's overflow:hidden.
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  {fields.map((field, idx) => (
                    <SortableLevelCard
                      key={ids[idx] ?? `tmp-${field.key}`}
                      id={ids[idx] ?? `tmp-${field.key}`}
                      fieldName={field.name}
                      displayedNumber={idx + 1}
                      onEdit={() => openEdit(ids[idx], idx)}
                      onRemove={() => confirmDelete(() => removeAt(idx))}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => {
                const newId = newDndId();
                add({ level_type: "trivia", config: {} });
                setIds((prev) => [...prev, newId]);
                // Mark as a draft so Cancel will discard the empty level.
                draftIdsRef.current.add(newId);
                // Open the modal for the newly added level so the user fills
                // it in immediately.
                setEditingId(newId);
              }}
            >
              {t("event.thematic.add_level")}
            </Button>

            {/* Single Modal hosting the per-level form. Declared inside the
                Form.List render-prop so Form context flows in via React (the
                Modal portals via DOM but context goes by React tree). */}
            <Modal
              visible={!!editingField}
              title={
                editingField
                  ? `${t("event.thematic.level_number")} ${editingIdx + 1}`
                  : ""
              }
              // X (close button) and Esc both run onCancel — same as the
              // Cancel button. maskClosable stays false so a stray click on
              // the backdrop doesn't dismiss in-flight edits.
              closable
              keyboard
              maskClosable={false}
              onCancel={onCancelModal}
              width={640}
              destroyOnClose={false}
              footer={[
                <Button
                  key="delete"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (editingIdx < 0) return;
                    confirmDelete(() => removeAt(editingIdx));
                  }}
                >
                  {t("event.thematic.delete_level")}
                </Button>,
                <Button key="cancel" onClick={onCancelModal}>
                  {t("event.thematic.cancel_level")}
                </Button>,
                <Button key="save" type="primary" onClick={onSaveModal}>
                  {t("event.thematic.save_level")}
                </Button>,
              ]}
            >
              {editingField && (
                <LevelFormFields
                  fieldName={editingField.name}
                  languages={languages}
                />
              )}
            </Modal>
          </>
        );
      }}
    </Form.List>
  );
};
